import rootStore from '../../RootStore';
import { E_RISK_TYPE, GLOBAL_INDEXES } from '../../interfaces';
import { getRiskShortName } from '../../utils/riskUtils';

export function isUrlRefDataReady(): boolean {
    return (
        Boolean(rootStore.countryStore.countries?.countries) &&
        (rootStore.risksStore.allDimensions || []).length > 0
    );
}

function slugifyRiskToken(value: string): string {
    return (value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

export function riskIdToAbbr(type: E_RISK_TYPE, id: number): string {
    const short = getRiskShortName(type, id);
    return short ? slugifyRiskToken(short) : null;
}

function riskAbbrEntries(type: E_RISK_TYPE): Array<{ id: number; slug: string }> {
    const risks = rootStore.risksStore;
    let ids: number[] = [];
    switch (type) {
        case E_RISK_TYPE.DIMENSIONS:
            ids = (risks.allDimensions || []).map(d => d.id);
            break;
        case E_RISK_TYPE.CLIENT_FACING_INDICATORS:
            ids = Object.values(risks.clientFacingIndicators || {}).map(
                c => c.id
            );
            break;
        case E_RISK_TYPE.CONTINGENT:
            ids = (risks.contingentRisks || []).map(c => c.id);
            break;
        default:
            ids = [];
    }
    return ids
        .map(id => ({ id, slug: riskIdToAbbr(type, id) }))
        .filter((e): e is { id: number; slug: string } => e.slug != null);
}

export function abbrToRiskId(type: E_RISK_TYPE, abbr: string): number {
    if (!abbr) {
        return null;
    }
    const target = slugifyRiskToken(abbr);
    const match = riskAbbrEntries(type).find(e => e.slug === target);
    return match ? match.id : null;
}

export function countryIdToIso(id: number) {
    return rootStore.countryStore.countries.allCountries[id].abbreviation_short;
}

export function isoToCountryId(iso: string) {
    if (!iso) {
        return null;
    }
    const target = iso.trim().toUpperCase();
    const country = rootStore.countryStore.countries.allCountriesArr.find(
        c => (c.abbreviation_short || '').toUpperCase() === target
    );
    return country?.id;
}

export function encodeCountryList(ids: number[]): string {
    return (ids || [])
        .map(countryIdToIso)
        .filter((iso): iso is string => Boolean(iso))
        .join(',');
}

export function decodeCountryList(value: string): number[] {
    if (!value) {
        return [];
    }
    return value
        .split(',')
        .map(token => isoToCountryId(token))
        .filter((id): id is number => id != null);
}

const COMMODITY_TO_INDICATOR: Record<string, GLOBAL_INDEXES> = {
    oil: 'oil_price',
    gold: 'gold_price',
};
const INDICATOR_TO_COMMODITY: Record<string, string> = {
    oil_price: 'oil',
    gold_price: 'gold',
};

export function commodityToIndicator(token: string): GLOBAL_INDEXES {
    return COMMODITY_TO_INDICATOR[(token || '').toLowerCase()] ?? null;
}
export function indicatorToCommodity(indicator: string): string {
    return INDICATOR_TO_COMMODITY[indicator] ?? null;
}

const MACRO_TO_INDICATOR: Record<string, GLOBAL_INDEXES> = {
    gdp: 'real_gdp_growth',
    cpi: 'cpi_inflation',
};
const INDICATOR_TO_MACRO: Record<string, string> = {
    real_gdp_growth: 'gdp',
    cpi_inflation: 'cpi',
};

export function macroToIndicator(token: string): GLOBAL_INDEXES {
    return MACRO_TO_INDICATOR[(token || '').toLowerCase()] ?? null;
}
export function indicatorToMacro(indicator: string): string {
    return INDICATOR_TO_MACRO[indicator] ?? null;
}
